import { useEffect, useState } from "react";
import { Facebook, Instagram, Linkedin, Twitter, Trash2, Cloud, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, LoadingBlock } from "@/components/common/States";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

const NET_ICON = { instagram: Instagram, linkedin: Linkedin, facebook: Facebook, x: Twitter };
const NETS = [
  { code: "instagram", label: "Instagram" },
  { code: "linkedin", label: "LinkedIn" },
  { code: "facebook", label: "Facebook" },
  { code: "x", label: "X (Twitter)" },
];

const SOURCES = [
  { code: "gdrive", label: "Google Drive" },
  { code: "onedrive", label: "OneDrive" },
  { code: "dropbox", label: "Dropbox" },
];

export default function SocialAccounts() {
  const { role } = useAuth();
  const canWrite = can(role, "social.write");
  const [accounts, setAccounts] = useState(null);
  const [sources, setSources] = useState(null);
  const [open, setOpen] = useState(false);
  const [srcOpen, setSrcOpen] = useState(false);
  const [form, setForm] = useState({ network: "instagram", handle: "", display_name: "" });
  const [srcForm, setSrcForm] = useState({ kind: "gdrive", account_label: "" });
  // Which providers this deployment can actually authorise against. A network
  // without credentials must not offer a Connect button that leads to a
  // provider error page.
  const [providers, setProviders] = useState([]);

  const load = async () => {
    try {
      const [a, s, p] = await Promise.all([
        api.get("/social/accounts"),
        api.get("/social/sources"),
        api.get("/oauth/providers").catch(() => ({ data: { items: [] } })),
      ]);
      setAccounts(a.data.items); setSources(s.data.items);
      setProviders(p.data.items || []);
    } catch { setAccounts([]); setSources([]); }
  };
  useEffect(() => { load(); }, []);

  // The OAuth callback sends the browser back here with the outcome in the
  // query string. Without this the user returns to a page that looks exactly
  // as it did before, whether it worked or not.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const connected = params.get("connected");
    const failed = params.get("error");
    if (connected) {
      const n = params.get("accounts");
      toast.success(`${connected} connected${n ? ` — ${n} account${n === "1" ? "" : "s"}` : ""}`);
    } else if (failed) {
      toast.error(failed);
    }
    if (connected || failed) {
      // Clear them, so a refresh does not replay the message.
      params.delete("connected"); params.delete("error"); params.delete("accounts");
      setParams(params, { replace: true });
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Start a real authorisation.
   *
   * The provider owns the next screen, so this leaves the app entirely rather
   * than opening a popup -- a popup that a browser blocks looks like a button
   * that does nothing, which is the failure this whole page is being fixed for.
   */
  async function connectVia(providerKey) {
    try {
      const { data } = await api.post(`/oauth/${providerKey}/start`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  function isLive(key) {
    return providers.some((p) => p.key === key && p.configured);
  }

  async function connect() {
    if (!form.handle.trim()) return toast.error("Handle is required");
    try {
      await api.post("/social/accounts", form);
      toast.success(`Added @${form.handle} as a simulated account`);
      setOpen(false); setForm({ network: "instagram", handle: "", display_name: "" });
      load();
    } catch (e) { toast.error(humanError(e)); }
  }
  async function disconnect(id) {
    if (!window.confirm("Disconnect this account?")) return;
    try { await api.delete(`/social/accounts/${id}`); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }
  async function connectSrc() {
    if (!srcForm.account_label.trim()) return toast.error("Label is required");
    try {
      await api.post("/social/sources", srcForm);
      toast.success("Connected");
      setSrcOpen(false); setSrcForm({ kind: "gdrive", account_label: "" });
      load();
    } catch (e) { toast.error(humanError(e)); }
  }
  async function disconnectSrc(id) {
    if (!window.confirm("Disconnect this source?")) return;
    try { await api.delete(`/social/sources/${id}`); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }

  return (
    <div>
      <PageHeader
        title="Social accounts"
        subtitle={
          providers.some((pr) => pr.kind === "social" && pr.configured)
            ? "Connect the networks your team publishes to."
            : "No network is configured for live publishing yet, so accounts added here use a simulated adapter and nothing reaches a real feed."
        }
        actions={
          canWrite ? (
            <>
              {providers.filter((pr) => pr.kind === "social" && pr.configured).map((pr) => (
                <button
                  key={pr.key}
                  onClick={() => connectVia(pr.key)}
                  data-testid={`connect-${pr.key}`}
                  className="hs-btn-primary text-sm"
                >
                  Connect {pr.label}
                </button>
              ))}
              <Dialog open={srcOpen} onOpenChange={setSrcOpen}>
                <DialogTrigger asChild>
                  <button data-testid="add-source-button" className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">Add content source</button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Connect content source</DialogTitle>
                    <DialogDescription>Import media from your drive.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Source</label>
                      <select value={srcForm.kind} onChange={(e) => setSrcForm({ ...srcForm, kind: e.target.value })} data-testid="source-kind-select" className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm">
                        {SOURCES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Account label</label>
                      <input value={srcForm.account_label} onChange={(e) => setSrcForm({ ...srcForm, account_label: e.target.value })} data-testid="source-label-input" className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" placeholder="team@company.com" />
                    </div>
                  </div>
                  <DialogFooter>
                    <button onClick={() => setSrcOpen(false)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm">Cancel</button>
                    <button data-testid="source-connect-confirm" onClick={connectSrc} className="hs-btn-primary text-sm">Connect</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button data-testid="connect-account-button" className="hs-btn-primary text-sm">Connect social account</button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Connect a social account</DialogTitle>
                    <DialogDescription>Using the simulated adapter — this creates a working connection so you can schedule and publish end-to-end.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Network</label>
                      <select value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} data-testid="account-network-select" className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm">
                        {NETS.map(n => <option key={n.code} value={n.code}>{n.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Handle</label>
                      <input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} data-testid="account-handle-input" className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" placeholder="yourbrand" />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Display name</label>
                      <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="account-display-input" className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" placeholder="Your Brand" />
                    </div>
                  </div>
                  <DialogFooter>
                    <button onClick={() => setOpen(false)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm">Cancel</button>
                    <button data-testid="account-connect-confirm" onClick={connect} className="hs-btn-primary text-sm">Connect</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : null
        }
      />

      <section className="mb-8">
        <h2 className="mb-3 font-display text-lg font-semibold text-[#0B2B33]">Networks</h2>
        <div className="rounded-xl border border-[#E4E9EF] bg-white">
          {accounts === null ? <div className="p-6"><LoadingBlock rows={3} /></div> :
            accounts.length === 0 ? <div className="p-8"><EmptyState title="No accounts connected" body="Connect at least one to start publishing." /></div> :
              <ul className="divide-y divide-[#E4E9EF]">
                {accounts.map(a => {
                  const Icon = NET_ICON[a.network] || Cloud;
                  return (
                    <li key={a.id} data-testid={`account-row-${a.id}`} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#EEF3FB] text-[#1F5FD8]"><Icon className="h-5 w-5" /></span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B2B33]">{a.display_name}</div>
                          <div className="text-xs text-[#8A97A0]">@{a.handle} · {(a.followers || 0).toLocaleString()} followers</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="hs-chip bg-[#E8FBF1] text-[#0B5D3C]">{a.status}</span>
                        <span className="hs-chip">SIMULATED</span>
                        {canWrite ? <button data-testid={`disconnect-${a.id}`} onClick={() => disconnect(a.id)} className="text-[#E3252A] hover:bg-[#FFE6E6] rounded p-1"><Trash2 className="h-4 w-4" /></button> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
          }
        </div>
      </section>

      <section>
        <h2 className="mb-3 font-display text-lg font-semibold text-[#0B2B33]">Content sources</h2>
        <div className="rounded-xl border border-[#E4E9EF] bg-white">
          {sources === null ? <div className="p-6"><LoadingBlock rows={2} /></div> :
            sources.length === 0 ? <div className="p-8"><EmptyState title="No sources yet" body="Connect a drive to import media." /></div> :
              <ul className="divide-y divide-[#E4E9EF]">
                {sources.map(s => (
                  <li key={s.id} data-testid={`source-row-${s.id}`} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#F6F8FA] text-[#43535E]"><HardDrive className="h-5 w-5" /></span>
                      <div>
                        <div className="text-sm font-semibold capitalize text-[#0B2B33]">{s.kind === "gdrive" ? "Google Drive" : s.kind}</div>
                        <div className="text-xs text-[#8A97A0]">{s.account_label}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="hs-chip bg-[#E8FBF1] text-[#0B5D3C]">{s.status}</span>
                      {canWrite ? <button data-testid={`disconnect-source-${s.id}`} onClick={() => disconnectSrc(s.id)} className="text-[#E3252A] hover:bg-[#FFE6E6] rounded p-1"><Trash2 className="h-4 w-4" /></button> : null}
                    </div>
                  </li>
                ))}
              </ul>
          }
        </div>
      </section>
    </div>
  );
}
